import { convertToUtc } from "@gigpro/utils/datetime";
import type {
  StafferTimeAndAttendanceStatsRetrieveParams,
  StafferTimeAndAttendanceStatsRetrieveStatuses,
} from "@sidegigllc/api/client";
import { addMinutes, endOfDay, min as earliest, parse, startOfDay, subDays, subMinutes } from "date-fns";
import { ACTIVE_SHIFT_WINDOW } from "utils/GigUtil";

export interface EventViewContext {
  type: "event";
  eventId: string;
  positionId?: number;
  statuses?: StafferTimeAndAttendanceStatsRetrieveStatuses;
}

export interface DateViewContext {
  type: "date";
  locationId: number;
  /** MM-DD-YYYY */
  date: string;
  timezone: string;
  positionId?: number;
  statuses?: StafferTimeAndAttendanceStatsRetrieveStatuses;
}

export interface LocationViewContext {
  type: "location";
  locationId: number;
  mine?: boolean;
  positionId?: number;
  statuses?: StafferTimeAndAttendanceStatsRetrieveStatuses;
}

export type TimeAndAttendanceViewContext = EventViewContext | DateViewContext | LocationViewContext;

/** Pure: callers layer extra filters (e.g. active window) onto the result. */
export function buildTimeAndAttendanceScope(
  ctx: TimeAndAttendanceViewContext,
): StafferTimeAndAttendanceStatsRetrieveParams {
  const base = scopeFor(ctx);
  if (ctx.positionId != null) base.position_id = ctx.positionId;
  if (ctx.statuses) base.statuses = ctx.statuses;
  return base;
}

function scopeFor(ctx: TimeAndAttendanceViewContext): StafferTimeAndAttendanceStatsRetrieveParams {
  switch (ctx.type) {
    case "event":
      return eventScope(ctx);
    case "date":
      return dateScope(ctx);
    case "location":
      return ctx.mine ? { location_id: [ctx.locationId], mine: true } : { location_id: [ctx.locationId] };
  }
}

function eventScope(ctx: EventViewContext): StafferTimeAndAttendanceStatsRetrieveParams {
  return { event_id: ctx.eventId };
}

function dateScope(ctx: DateViewContext): StafferTimeAndAttendanceStatsRetrieveParams {
  const day = parse(ctx.date, "MM-dd-yyyy", new Date());
  return {
    location_id: [ctx.locationId],
    start_time__gte: convertToUtc(startOfDay(day), ctx.timezone),
    start_time__lte: convertToUtc(endOfDay(day), ctx.timezone),
  };
}

/**
 * Layers the live active window onto a scope. `start_time__lte` is intersected, not replaced: on a
 * past day's page the lead edge sits beyond that day, so replacing it would count the next day's
 * shifts while the list below shows only this day's.
 */
export function withActiveShiftWindow(
  scope: StafferTimeAndAttendanceStatsRetrieveParams,
  now: Date,
): StafferTimeAndAttendanceStatsRetrieveParams {
  const leadEdge = addMinutes(now, ACTIVE_SHIFT_WINDOW.beforeMinutes);
  return {
    ...scope,
    ...(!scope.event_id && !scope.start_time__gte && { start_time__gte: subDays(now, 1) }),
    start_time__lte: scope.start_time__lte ? earliest([scope.start_time__lte, leadEdge]) : leadEdge,
    end_time__gte: subMinutes(now, ACTIVE_SHIFT_WINDOW.afterMinutes),
  };
}
